import { Link } from "react-router-dom";
import { Copy, Eye, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import type { VaultEntry } from "../../../lib/vault";
import { StatusPill, fmtDate, useCopyToast } from "../../ui";
import { Pop, useClosePops } from "../../shell/Pop";
import { iconFor, kindLabelKey } from "./kinds";

/**
 * Seven tracks: what it opens, whose it is, where, as whom, whether a password
 * is on file, when it last changed, and the menu. The constant lives with the
 * row and the list head imports it - two separate track lists drift apart the
 * first time somebody adds a column.
 */
export const COLS = "minmax(0,1.9fr) 1.1fr 1.15fr .95fr .8fr .75fr 44px";

export function VaultRow({
  entry,
  canManage,
  onReveal,
  onEdit,
  onDelete,
}: {
  entry: VaultEntry;
  canManage: boolean;
  onReveal: (entry: VaultEntry) => void;
  onEdit: (entry: VaultEntry) => void;
  onDelete: (entry: VaultEntry) => void;
}) {
  const t = useT();
  const copy = useCopyToast();
  const close = useClosePops();
  const Icon = iconFor(entry.kind);

  function run(fn: () => void) {
    close();
    fn();
  }

  return (
    <div className="crm-list-row relative" style={{ gridTemplateColumns: COLS }}>
      <span className="flex min-w-0 items-center gap-3">
        <span className="crm-av crm-av-soft" aria-hidden>
          <Icon className="size-4" />
        </span>
        <span className="min-w-0">
          <span className="crm-prow-n crm-trunc block">{entry.title}</span>
          <span className="crm-prow-s crm-trunc block">{t(kindLabelKey(entry.kind))}</span>
        </span>
      </span>

      <span className="crm-trunc text-[12.5px]">
        {entry.clientId ? (
          <Link to={`/clients/${entry.clientId}`} className="crm-trunc block hover:underline">
            {entry.clientName ?? t("vault.client")}
          </Link>
        ) : (
          <span className="crm-mut">-</span>
        )}
      </span>

      {/* Host and port together: they are one address, and a port in a column of
          its own would be six characters of empty space on most rows. */}
      <span className="crm-mut crm-trunc text-[12.5px]">
        {entry.host ? `${entry.host}${entry.port ? `:${entry.port}` : ""}` : "-"}
      </span>

      <span className="crm-trunc text-[12.5px]">{entry.username ?? <span className="crm-mut">-</span>}</span>

      <span>
        {/* THREE states, not two, and the middle one is the reason.
            Everything imported out of a Perfex project custom field arrives as
            one free-text blob in `notes` with nothing in `secret` - a password
            IS on file, just not in the password field. A two-state pill would
            print "none" beside twenty-five entries that hold credentials, which
            is the most dangerous thing this list could say. */}
        {entry.hasSecret ? (
          <StatusPill tone="ok">{t("vault.stored")}</StatusPill>
        ) : entry.hasNotes ? (
          <StatusPill tone="wt">{t("vault.inNotes")}</StatusPill>
        ) : (
          <StatusPill tone="mut">{t("vault.noSecret")}</StatusPill>
        )}
      </span>

      <span className="crm-mut crm-tnum text-[12.5px]">{fmtDate(entry.updatedAt)}</span>

      <span className="relative z-[1] flex items-center justify-self-end">
        {canManage ? (
          <button
            type="button"
            className="crm-cb"
            onClick={() => onReveal(entry)}
            title={t("vault.reveal")}
            aria-label={t("vault.reveal")}
          >
            <Eye className="size-4" />
          </button>
        ) : null}

        <Pop
          trigger={(open) => (
            <button
              type="button"
              className={`crm-cb${open ? " is-on" : ""}`}
              title={t("common.more")}
              aria-label={t("common.more")}
              aria-haspopup="menu"
              aria-expanded={open}
            >
              <MoreHorizontal className="size-4" />
            </button>
          )}
        >
          {entry.username ? (
            <button type="button" className="crm-mi" onClick={() => run(() => copy(entry.username ?? ""))}>
              <Copy className="size-4" />
              {t("vault.copyUsername")}
            </button>
          ) : null}

          {canManage ? (
            <>
              {entry.username ? <div className="crm-menu-sep" /> : null}
              <button type="button" className="crm-mi" onClick={() => run(() => onEdit(entry))}>
                <Pencil className="size-4" />
                {t("vault.edit")}
              </button>
              <div className="crm-menu-sep" />
              <button type="button" className="crm-mi crm-mi-danger" onClick={() => run(() => onDelete(entry))}>
                <Trash2 className="size-4" />
                {t("common.delete")}
              </button>
            </>
          ) : null}
        </Pop>
      </span>
    </div>
  );
}
